<template>
  <v-card elevation="0">
    <v-card-title @click="toggleImportableUsers" class="toggleHeader" data-cy="adminUser__importList">
      <v-icon small class="mr-3">fas {{ showImportableUsers ? "fa-chevron-down" : "fa-chevron-right" }}</v-icon>
      <span class="headline font-weight-thin">
        Users Available for Import (
        <span v-if="importUserListLoading"><v-icon>fas fa-spin fa-spinner</v-icon></span>
        <span v-else>{{ importUserList.length }}</span>
        )
      </span>
    </v-card-title>
    <div v-if="showImportableUsers">
      <v-alert v-if="!isServiceAreaActive" type="warning" outlined dense class="mx-4 mt-4">
        This Service Area is inactive. User import is not available. To re-enable importing, reactivate this Service Area.
      </v-alert>
      <v-alert v-else-if="groupNotFound" type="info" outlined dense class="mx-4 mt-4">
        The required Azure AD group for this Service Area could not be found. Users cannot be imported.
      </v-alert>
      <template v-else>
      <v-text-field
        hide-details
        label="Filter Users..."
        outlined
        single-line
        v-model="searchUsers"
        class="mb-4 mx-4 pt-4"
        style="max-width: 50%"
        clearable
        dense
      >
        <template v-slot:prepend-inner>
          <v-icon class="mr-2">fas fa-search</v-icon>
        </template>
      </v-text-field>
      <v-data-table
        :headers="headerList"
        :items="importUserList"
        :loading="loading"
        :search="searchUsers"
        :custom-filter="customFilter"
        :items-per-page="5"
        :footer-props="{
          'items-per-page-options': [5, 10, 25, 50, -1],
          'items-per-page-text': 'Users per page:',
        }"
        :sort-by="['surname']"
        :custom-sort="customSort"
        must-sort
        dense
      >
        <template v-slot:item="{ item }">
          <tr>
            <td>
              <v-tooltip bottom v-if="isEmailMissing(item.mail)">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    color="warning"
                    v-bind="attrs"
                    v-on="on"
                    class="mr-2"
                    aria-label="Warning: Email not configured in Azure AD"
                    >fas fa-exclamation-triangle</v-icon
                  >
                </template>
                <span>Cannot import - Email not configured in Azure AD</span>
              </v-tooltip>
              {{ item.displayName }}
            </td>
            <td>{{ item.mail }}</td>
            <td>{{ item.userPrincipalName }}</td>
            <td>
              <template v-if="importingUser === item.id">
                <v-icon small>fas fa-spin fa-spinner</v-icon>
              </template>
              <template v-else-if="isEmailMissing(item.mail)">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      small
                      color="grey"
                      v-bind="attrs"
                      v-on="on"
                      aria-label="Cannot import - Email not configured in Azure AD"
                      >fas fa-ban</v-icon
                    >
                  </template>
                  <span>Cannot import - Email not configured in Azure AD</span>
                </v-tooltip>
              </template>
              <template v-else>
                <v-btn icon small @click="importUser(item)" :aria-label="`Import ${item.displayName}`">
                  <v-icon small>fas fa-arrow-down</v-icon>
                </v-btn>
              </template>
            </td>
          </tr>
        </template>
      </v-data-table>
      </template>
    </div>
  </v-card>
</template>

<script>
import { debounce } from "lodash";
import { mapActions, mapGetters, mapState } from "vuex";

export default {
  computed: {
    ...mapState({
      importUserListRaw: state => state.admin.user.import.list,
      importUserListLoading: state => state.admin.user.import.loadingList,
    }),
    ...mapGetters({
      serviceAreaMap: "admin/serviceArea/getServiceAreaMap",
    }),
    isServiceAreaActive() {
      return this.serviceAreaMap[this.serviceAreaId]?.active !== 0;
    },
    hasAlert() {
      return !this.isServiceAreaActive || this.groupNotFound;
    },
    importUserList() {
      // Add precomputed searchable field for efficient O(1) filtering
      return this.importUserListRaw.map(user => ({
        ...user,
        _searchable: [user.displayName, user.givenName, user.surname, user.mail, user.userPrincipalName]
          .filter(Boolean)
          .join(" ")
          .toLowerCase(),
      }));
    },
  },
  data() {
    return {
      headerList: [
        {
          text: "",
          value: "_searchable",
          sortable: false,
          class: "d-none",
        },
        {
          text: "Name",
          value: "surname",
        },
        {
          text: "Email Address",
          value: "mail",
        },
        { text: "UPN", value: "userPrincipalName" },
        {
          sortable: false,
          text: "Import",
        },
      ],
      groupNotFound: false,
      loading: true,
      searchUsers: null,
      showImportableUsers: false,
      importingUser: null,
    };
  },
  methods: {
    customFilter(value, search) {
      // Use precomputed _searchable field for O(1) filtering per invocation
      if (!search) return true;
      return value && typeof value === "string" && value.includes(search.toLowerCase());
    },
    customSort(items, sortBy, sortDesc) {
      // Custom sort: importable users first, then unimportable users, then by the selected sort column (defaulting to surname)
      return [...items].sort((a, b) => {
        const aHasEmail = !this.isEmailMissing(a.mail);
        const bHasEmail = !this.isEmailMissing(b.mail);

        // If one has email and the other doesn't, prioritize the one with email
        if (aHasEmail && !bHasEmail) return -1;
        if (!aHasEmail && bHasEmail) return 1;

        // Both have same email status, sort by the specified column
        const sortByColumn = sortBy[0] || "surname";
        const isDescending = sortDesc[0] || false;

        const aValue = a[sortByColumn] || "";
        const bValue = b[sortByColumn] || "";

        if (isDescending) {
          return bValue.localeCompare(aValue);
        } else {
          return aValue.localeCompare(bValue);
        }
      });
    },
    isEmailMissing(email) {
      // Mirror backend validation: email must be non-empty string after trimming
      return !(typeof email === "string" && email.trim().length > 0);
    },
    escapeHtml(text) {
      const div = document.createElement("div");
      div.textContent = text;
      return div.innerHTML;
    },
    async importUser(user) {
      this.importingUser = user.id;

      try {
        await this.importUserIntoContinuum({
          id: user.id,
          serviceAreaId: this.serviceAreaId,
        });

        // Import succeeded - show success message
        this.$notifySuccess(`${this.escapeHtml(user.displayName)} has been imported successfully.`);

        // Try to refresh lists, but don't fail the whole operation if refresh fails
        try {
          // Show table loading indicator during refresh
          this.loading = true;

          const continuumListPromise = this.fetchContinuumList([
            {
              comparator: "=",
              name: "serviceAreaId",
              value: this.serviceAreaId,
            },
          ]);

          const importListPromise = this.fetchImportList(this.serviceAreaId);

          await Promise.all([continuumListPromise, importListPromise]);
        } catch (refreshError) {
          console.error("Failed to refresh lists after import:", refreshError);
          this.$notifyError({
            message: `${this.escapeHtml(
              user.displayName
            )} was imported successfully, but the user list could not be refreshed. Please refresh the page to see the updated list.`,
            timeout: 0,
          });
        } finally {
          // Clear table loading indicator
          this.loading = false;
        }
      } catch (e) {
        console.error(e);

        // Check if this is the email validation error
        const isEmailError = e.response?.status === 400 && e.response?.data?.code === "AZURE_USER_MISSING_EMAIL";

        if (isEmailError) {
          this.$notifyError({
            message:
              "User cannot be imported because their email address is not configured in Azure AD.<br>Please submit a Jira ticket to HelpDesk to request email configuration for this user.",
            timeout: 0, // Persistent - stays until user closes
          });
        } else {
          this.$notifyError({
            message: `There was an error importing ${this.escapeHtml(
              user.displayName
            )}. This user may already exist in Continuum. Please check the user list.`,
            timeout: 0,
          });
        }
      } finally {
        this.importingUser = null;
      }
    },
    toggleImportableUsers() {
      this.showImportableUsers = !this.showImportableUsers;
    },
    ...mapActions({
      clearImportList: "admin/user/clearImportList",
      fetchContinuumList: "admin/user/fetchList",
      fetchImportList: "admin/user/importUserListByServiceArea",
      importUserIntoContinuum: "admin/user/import",
    }),
  },
  props: {
    serviceAreaId: {
      required: true,
      type: Number,
    },
  },
  watch: {
    hasAlert: {
      immediate: true,
      handler(val) {
        if (val) this.showImportableUsers = true;
      },
    },
    isServiceAreaActive(isActive) {
      if (!isActive) {
        this.clearImportList();
        this.groupNotFound = false;
      }
    },
    serviceAreaId: {
      handler: debounce(async function(value) {
        this.loading = true;
        this.groupNotFound = false;
        if (!this.isServiceAreaActive) {
          this.clearImportList();
          this.loading = false;
          return;
        }
        try {
          await this.fetchImportList(value);
        } catch (e) {
          if (e.response?.data?.code === "AZURE_GROUP_NOT_FOUND") {
            this.groupNotFound = true;
          } else {
            this.$notifyError({
              message: "There was an error loading users available for import. Please try again.",
              timeout: 0,
            });
          }
        } finally {
          if (this.serviceAreaId === value) {
            this.loading = false;
          }
        }
      }, 500),
      immediate: true,
    },
  },
};
</script>

<style scoped>
.toggleHeader {
  cursor: pointer;
}
</style>
